import Button from '@allesauto/core/button';
import Headline from '@allesauto/core/headline';
import NextImage from '@allesauto/core/nextImage';
import NextLink from '@allesauto/core/nextLink';
import TextLink from '@allesauto/core/textLink';
import { getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { FieldTypeAsset, FieldTypeLink } from '@allesauto/utils/storyblok';
import clsx from 'clsx';

enum Colors {
  BLACK = 'BLACK',
  GREY = 'GREY',
  DARK_GREY = 'DARK_GREY',
  PURPLE = 'PURPLE',
  WHITE = 'WHITE',
  GREEN = 'GREEN',
}

export type ColorsObject = typeof Colors;

export interface TextImageBoxProps {
  headline: string;
  headlineColor: keyof ColorsObject;
  headlineFontSize?: 'title' | 'headline2' | 'headline3' | 'headline4';
  description: string;
  descriptionColor: keyof ColorsObject;
  background: keyof ColorsObject;
  image?: FieldTypeAsset;
  buttonLabel?: string;
  buttonLink: FieldTypeLink;
  buttonType?: 'TEXT' | 'BUTTON';
  _uid: string;
  headlineTag: 2 | 3 | 4;
}

const cssClasses = {
  container:
    'p-[24px] pr-0 md:p-[40px] lg:p-[48px] lg:pt-[24px] md:pr-0 lg:pr-0 flex align-center justify-between gap-[16px] flex-1',
  headline: 'mb-[40px]',
  description: 'mb-[80px]',
  imageContainer: 'relative min-w-[200px] hidden md:block w-[50%]',
  mobileImageContainer:
    'relative  min-w-[60px] w-full max-w-[140px] md:hidden ',
  textContainer: 'flex align-center gap-[8px] justify-between',
  textContent: 'sm:max-w-[60%] md:max-w-[100%]',
  content: 'w-full md:w-[50%] pt-[16px]',
  buttonContainer: 'sm:pr-[24px] sm:mt-10px md:mt-0 md:pr-0',
  redButton: 'w-full md:w-[auto]',
};

export const themes = {
  [Colors.BLACK]: {
    background: 'bg-black monochrome:bg-black',
    text: 'text-black monochrome:text-black',
  },
  [Colors.PURPLE]: {
    background: 'bg-purple80 monochrome:bg-purple80',
    text: 'text-purple80 monochrome:text-purple80',
  },
  [Colors.DARK_GREY]: {
    background: 'bg-grey80 monochrome:bg-grey80',
    text: 'text-grey60 monochrome:text-grey60',
  },
  [Colors.WHITE]: {
    background: 'bg-white monochrome:bg-white',
    text: 'text-white monochrome:text-white',
  },
  [Colors.GREEN]: {
    background: 'bg-green60 monochrome:bg-green60',
    text: 'text-green60 monochrome:text-green60',
  },
  [Colors.GREY]: {
    background: 'bg-grey10 monochrome:bg-grey10',
    text: 'text-grey20 monochrome:text-grey20',
  },
};

const TextImageBox = (props: TextImageBoxProps) => {
  const {
    headline,
    headlineColor,
    headlineFontSize,
    description,
    descriptionColor,
    buttonLabel = '',
    buttonLink,
    buttonType = 'BUTTON',
    background,
    image,
    headlineTag,
  } = props;

  const buttonHref = getUrlFromStoryblokLink(buttonLink);

  const buttons = {
    TEXT: <TextLink text={buttonLabel} href={buttonHref} />,
    BUTTON: (
      <NextLink href={buttonHref}>
        <Button text={buttonLabel} customClassName={cssClasses.redButton} />
      </NextLink>
    ),
  };

  return (
    <div className={clsx(cssClasses.container, themes[background].background)}>
      <div className={cssClasses.content}>
        <div className={cssClasses.textContainer}>
          <div className={cssClasses.textContent}>
            <Headline
              headlineTag={headlineTag}
              headlineText={headline}
              className={clsx(
                cssClasses.headline,
                themes[headlineColor].text,
                headlineFontSize && `!text-${headlineFontSize}`
              )}
            />

            <p
              className={clsx(
                cssClasses.description,
                themes[descriptionColor].text
              )}
            >
              {description}
            </p>
          </div>
          {image?.filename && (
            <div className={cssClasses.mobileImageContainer}>
              <NextImage
                alt={image.alt}
                src={image.filename}
                layout="fill"
                objectFit="contain"
                objectPosition="right"
              />
            </div>
          )}
        </div>

        {buttonLabel && (
          <div className={cssClasses.buttonContainer}>
            {buttons[buttonType]}
          </div>
        )}
      </div>
      {image?.filename && (
        <div className={cssClasses.imageContainer}>
          <NextImage
            alt={image.alt}
            src={image.filename}
            layout="fill"
            objectFit="contain"
            objectPosition="right"
          />
        </div>
      )}
    </div>
  );
};

export default TextImageBox;
